import React, { useState, useMemo } from "react";

export default function GenericBookChapter({ data }) {
  const [copiedId, setCopiedId] = useState(null);

  // --- defensas básicas ---
  const safeData = useMemo(() => {
    if (!data || typeof data !== "object") return { title: "Capítulo", sections: [] };
    const sections = Array.isArray(data.sections) ? data.sections : [];
    return { title: data.title || "Capítulo", sections };
  }, [data]);

  // copiar código
  const copyToClipboard = (text, id) => {
    if (!text) return;
    navigator.clipboard.writeText(text).then(() => {
      setCopiedId(id);
      setTimeout(() => setCopiedId(null), 2000);
    });
  };

  // helpers
  const toArray = (val) => {
    if (!val) return [];
    if (Array.isArray(val)) return val.filter(Boolean);
    if (typeof val === "string") {
      // párrafos por doble salto
      return val
        .split(/\n{2,}/g)
        .map((s) => s.trim())
        .filter(Boolean);
    }
    return [];
  };
  const ensureCodes = (sec) => {
    if (Array.isArray(sec.codes)) return sec.codes.filter(Boolean);
    if (typeof sec.code === "string" && sec.code.trim()) return [sec.code];
    return [];
  };
  const ensureLinks = (sec) =>
    Array.isArray(sec.links) ? sec.links.filter((l) => l?.url && l?.label) : [];

  const hasAnyContent = (paragraphs, bullets, codes, notes, faq) =>
    paragraphs.length > 0 || bullets.length > 0 || codes.length > 0 || notes.length > 0 || faq.length > 0;

  return (
    <main className="chapter">

      <article className="container">
        {/* Título */}
        <h1>{safeData.title}</h1>

        {/* Índice automático */}
        {safeData.sections.length > 0 && (
          <nav className="subindex">
            <h3>Contenido</h3>
            <ul>
              {safeData.sections.map((sec, i) => {
                const id = sec.id || sec.heading?.toLowerCase().replace(/\s+/g, "-") || `sec-${i}`;
                return (
                  <li key={id}>
                    <a href={`#${id}`}>{sec.heading || `Sección ${i + 1}`}</a>
                  </li>
                );
              })}
            </ul>
          </nav>
        )}

        {/* Render secciones */}
        {safeData.sections.map((sec, idx) => {
          const sectionId =
            sec.id || sec.heading?.toLowerCase().replace(/\s+/g, "-") || `sec-${idx}`;

          const paragraphs = toArray(sec.text);
          const notes = toArray(sec.note);
          const bullets = Array.isArray(sec.list) ? sec.list.filter(Boolean) : [];
          const codes = ensureCodes(sec);
          const links = ensureLinks(sec);
          const faq = Array.isArray(sec.faq) ? sec.faq.filter((q) => q?.q && q?.a) : [];

          return (
            <section key={sectionId} id={sectionId}>
              <h2>{sec.heading || `Sección ${idx + 1}`}</h2>

              {/* Texto */}
              {paragraphs.map((p, i) => (
                <p key={`p-${i}`}>
                  {p.split(/\n/).map((line, j, arr) => (
                    <React.Fragment key={`l-${j}`}>
                      {line}
                      {j < arr.length - 1 && <br />}
                    </React.Fragment>
                  ))}
                </p>
              ))}

              {/* Lista opcional */}
              {bullets.length > 0 && (
                <ul>
                  {bullets.map((li, i) => (
                    <li key={`li-${i}`}>{li}</li>
                  ))}
                </ul>
              )}

              {/* Links opcionales */}
              {links.length > 0 && (
                <div className="links">
                  {links.map((l, i) => (
                    <a key={`lnk-${i}`} className="btn" href={l.url} target="_blank" rel="noreferrer">
                      {l.label}
                    </a>
                  ))}
                </div>
              )}

              {/* Bloques de código (uno o varios) */}
              {codes.map((codeStr, i) => {
                const codeKey = `${sectionId}-code-${i}`;
                return (
                  <div className="codeblock" key={codeKey}>
                    <button className="copybtn" onClick={() => copyToClipboard(codeStr, codeKey)}>
                      {copiedId === codeKey ? "✓ Copiado" : "Copiar"}
                    </button>
                    <pre>
                      <code className="solidity">{codeStr}</code>
                    </pre>
                  </div>
                );
              })}

              {/* FAQ opcional */}
              {faq.length > 0 && (
                <div className="faq">
                  {faq.map((item, i) => (
                    <div className="qa" key={`qa-${i}`}>
                      <p><b>Q:</b> {item.q}</p>
                      <p><b>A:</b> {item.a}</p>
                    </div>
                  ))}
                </div>
              )}

              {/* Notas */}
              {notes.map((n, i) => (
                <p className="note" key={`note-${i}`}>{n}</p>
              ))}

              {/* 🔧 Antes ponías un placeholder "—". Lo quitamos.
                  Si quieres dejarlo, solo muéstralo cuando la sección esté REALMENTE vacía: */}
              {!hasAnyContent(paragraphs, bullets, codes, notes, faq) ? null : null}
            </section>
          );
        })}
      </article>

      {/* ===== Estilos ===== */}
      <style jsx>{`
  /* ⛔ Nada global aquí para no tocar el header */

  .chapter { 
    min-height: 100vh; 
    padding: 8px; 
    background: #fff;           /* fondo solo del capítulo */
  }

  .chapter .container { 
    max-width: 920px; 
    margin: 0 auto; 
    background:#fff;            /* fondo blanco solo del artículo */
    padding: 8px 12px; 
  
  }

  .chapter h1 { 
    font-size: 36px; 
    font-weight: 800; 
    text-align: center; 
    color: #111; 
    margin: 16px 0 24px; 
  }

  .chapter h2 { 
    font-size: 26px; 
    font-weight: 700; 
    color: #3b82f6; 
    margin: 18px 0 10px; 
  }

  .chapter p, .chapter li { 
    font-size: 18px; 
    line-height: 1.75; 
    text-align: justify; 
    color: #111; 
  }

  .chapter ul { padding-left: 22px; margin: 10px 0 14px; }
  .chapter li { margin: 6px 0; }

  .chapter .subindex { background: #f3f4f6; padding: 14px; margin-bottom: 20px; border-radius: 0px; }
  .chapter .subindex h3 { color: #3b82f6; margin: 0 0 8px; font-size: 18px; }
  .chapter .subindex ul { margin: 0; padding-left: 18px; }
  .chapter .subindex li { margin-bottom: 6px; }
  .chapter .subindex a { color: #0284c7; text-decoration: none; }
  .chapter .subindex a:hover { text-decoration: underline; }

  .chapter .links { display:flex; gap:10px; flex-wrap:wrap; margin: 8px 0 12px; }
  .chapter .btn { display:inline-flex; padding: 8px 12px; border:1px solid #d1d5db; border-radius:6px; color:#111; text-decoration:none; }
  .chapter .btn:hover { background:#f9fafb; }

  .chapter .codeblock { position: relative; background: #0a0a0a; border: 1px solid #333; border-radius: 0px; margin: 16px 0; }
  .chapter .codeblock pre { margin: 0; padding: 16px; overflow-x: auto; }
  .chapter .codeblock code { font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace; font-size: 15px; color: #d1fae5; white-space: pre; display: block; }
  .chapter .copybtn { position: absolute; top: 6px; right: 8px; background: #1e293b; color: #fff; border: none; padding: 6px 10px; font-size: 12px; cursor: pointer; border-radius: 0px; }
  .chapter .copybtn:hover { background: #334155; }

  .chapter .note { background: #f1f5f9; border-left: 4px solid #3b82f6; padding: 10px 12px; border-radius: 4px; font-size: 16px; margin-top: 8px; }
  .chapter .faq .qa { background:#f8fafc; border:1px solid #e2e8f0; border-radius:6px; padding:10px 12px; margin:10px 0; }
`}</style>

    </main>
  );
}
